/**
 * useUserSync — loads all user data from Supabase on login, saves on change.
 *
 * Handles: savedRecipes, cartMeals, flyerItems, myPickItems
 * (Pantry is handled separately in MyPantry.tsx)
 *
 * Call this once at the top of App.tsx so it runs for every page.
 */
import { useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useAppStore, SavedRecipe, FlyerItem, MyPickItem } from '@/hooks/useAppStore';
import type { Meal } from '@/lib/index';

// ── helpers ───────────────────────────────────────────────────────────────────
const SUPA_URL = 'https://pgjncthsfxsylmvkgibx.supabase.co';

function anonKey() {
  // public anon key — safe in frontend
  return 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBnam5jdGhzZnhzeWxtdmtnaWJ4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzcxMDEwMTEsImV4cCI6MjA5MjY3NzAxMX0.placeholder';
}

// ── Load saved recipes from Supabase ─────────────────────────────────────────
async function loadSavedRecipes(userId: string): Promise<SavedRecipe[]> {
  const { data, error } = await supabase
    .from('user_saved_recipes')
    .select('*')
    .eq('user_id', userId)
    .order('saved_at', { ascending: false });
  if (error) { console.error('[sync] load recipes:', error.message); return []; }
  return (data ?? []).map(row => ({
    id:           row.meal_id,
    name:         row.name,
    tagline:      row.tagline ?? '',
    meal_type:    row.meal_type ?? 'Dinner',
    day:          row.day ?? '',
    serves:       row.serves ?? 4,
    cook_time:    row.cook_time ?? '',
    method:       row.method ?? '',
    total_cost:   row.total_cost ?? 0,
    per_serving:  row.per_serving ?? 0,
    instructions: row.instructions ?? [],
    ingredients:  row.ingredients ?? [],
    sides:        row.sides ?? [],
    tip:          row.tip ?? '',
    savedAt:      row.saved_at,
    sourcePlan:   row.source_plan ?? undefined,
  } as SavedRecipe));
}

// ── Load grocery list from Supabase ──────────────────────────────────────────
async function loadGroceryList(userId: string) {
  const { data, error } = await supabase
    .from('user_grocery_lists')
    .select('cart_meals, flyer_items, my_pick_items')
    .eq('user_id', userId)
    .single();
  if (error && error.code !== 'PGRST116') {
    console.error('[sync] load grocery:', error.message);
  }
  return {
    cartMeals:    (data?.cart_meals    ?? []) as Meal[],
    flyerItems:   (data?.flyer_items   ?? []) as FlyerItem[],
    myPickItems:  (data?.my_pick_items ?? []) as MyPickItem[],
  };
}

// ── Save grocery list to Supabase (debounced) ─────────────────────────────────
export async function saveGroceryList(
  userId: string,
  cartMeals: Meal[],
  flyerItems: FlyerItem[],
  myPickItems: MyPickItem[],
) {
  const { error } = await supabase
    .from('user_grocery_lists')
    .upsert({
      user_id:       userId,
      cart_meals:    cartMeals,
      flyer_items:   flyerItems,
      my_pick_items: myPickItems,
      updated_at:    new Date().toISOString(),
    }, { onConflict: 'user_id' });
  if (error) console.error('[sync] save grocery:', error.message);
}

// ── Save a single recipe to Supabase ─────────────────────────────────────────
export async function saveRecipeToSupabase(userId: string, meal: Meal, sourcePlan?: string) {
  const { error } = await supabase
    .from('user_saved_recipes')
    .upsert({
      user_id:      userId,
      meal_id:      meal.id,
      name:         meal.name,
      tagline:      meal.tagline ?? '',
      meal_type:    meal.meal_type ?? 'Dinner',
      day:          meal.day ?? '',
      serves:       meal.serves ?? 4,
      cook_time:    meal.cook_time ?? '',
      method:       meal.method ?? '',
      total_cost:   meal.total_cost ?? 0,
      per_serving:  meal.per_serving ?? 0,
      instructions: meal.instructions ?? [],
      ingredients:  meal.ingredients ?? [],
      sides:        meal.sides ?? [],
      tip:          (meal as any).tip ?? '',
      source_plan:  sourcePlan ?? null,
      saved_at:     new Date().toISOString(),
    }, { onConflict: 'user_id,meal_id' });
  if (error) console.error('[sync] save recipe:', error.message);
}

// ── Delete a recipe from Supabase ─────────────────────────────────────────────
export async function deleteRecipeFromSupabase(userId: string, mealId: string) {
  const { error } = await supabase
    .from('user_saved_recipes')
    .delete()
    .eq('user_id', userId)
    .eq('meal_id', mealId);
  if (error) console.error('[sync] delete recipe:', error.message);
}

// ── The hook ──────────────────────────────────────────────────────────────────
export function useUserSync() {
  const { user } = useAuth();
  const {
    cartMeals, flyerItems, myPickItems, savedRecipes,
    setSavedRecipes, setTrips,
  } = useAppStore();

  const store = useAppStore;
  const prevUserId = useRef<string | null>(null);
  const saveTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── On login: pull all data from Supabase → store ─────────────────────────
  useEffect(() => {
    if (!user) {
      prevUserId.current = null;
      return;
    }
    if (prevUserId.current === user.id) return; // already loaded for this session
    prevUserId.current = user.id;

    (async () => {
      const [recipes, grocery] = await Promise.all([
        loadSavedRecipes(user.id),
        loadGroceryList(user.id),
      ]);

      // Merge: Supabase wins for saved recipes (authoritative)
      setSavedRecipes(recipes);

      // Merge grocery: if Supabase has more items use Supabase, else keep local
      const s = useAppStore.getState();
      const mergedCart  = grocery.cartMeals.length  >= s.cartMeals.length  ? grocery.cartMeals  : s.cartMeals;
      const mergedFlyer = grocery.flyerItems.length >= s.flyerItems.length  ? grocery.flyerItems : s.flyerItems;
      const mergedPicks = grocery.myPickItems.length>= s.myPickItems.length ? grocery.myPickItems: s.myPickItems;

      useAppStore.setState({
        cartMeals:   mergedCart,
        flyerItems:  mergedFlyer,
        myPickItems: mergedPicks,
      });
    })();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Debounced grocery sync: whenever cart/flyer/picks change ─────────────
  useEffect(() => {
    if (!user) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveGroceryList(user.id, cartMeals, flyerItems, myPickItems);
    }, 1500); // 1.5s debounce — don't spam on every keystroke
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [user?.id, cartMeals, flyerItems, myPickItems]); // eslint-disable-line react-hooks/exhaustive-deps
}
